import { cloneDeep } from 'lodash';
import { v4 as uuid } from 'uuid';

import { ExpenseFormValues } from '@ReactComponents/Shared/Expense/ExpenseEditor/IExpenseFormValues';

import { AbacusCurrency } from '@Shared/helpers';
import { ExpenseMetadata } from '@Shared/interfaces/ExpenseMetadata';
import { IExpenseModel, IImageModel } from '@Shared/models';

import { getMileageValue } from '../../utils/mileageValue';
import { Api as apiService } from '../api';
import type { IVendor } from './formFields';

export interface SplitSharedValues {
  amount: number;
  date?: number;
  suggested_category_id?: string;
  entered_currency_amount: string;
  entered_currency_code: string;
  card_transaction_exchange_rate?: number;
  vendor: string;
  venue: IVendor;
  venueId?: string;
  metadata?: ExpenseMetadata;
  bypassWarnings?: boolean;
  card_transaction_id?: string;
  image_id?: string | null;
  image?: IImageModel | null;
  images?: IImageModel[] | null;
  user_id?: string;
  company_currency_code?: string;
}

export type SplitBody = {
  shared: SplitSharedValues;
  splits: ExpenseFormValues[];
};

export abstract class SplitApi {
  static readonly splitTransactionsApiURL: string = '/api/web/split_transactions';

  static includeWithExpenses = [
    'include[user]',
    'include[user_profile]',
    'include[venue_info]',
    'include[image]',
    'include[images]',
    'include[category]',
    'include[expense_tags]',
    'include[metadata]',
    'include[manager]',
    'include[card_transaction]',
    'include[card]',
    'include[trip]'
  ].join('&');

  static createSplitExpense(splitBodyData: SplitBody, bypassWarnings?: boolean) {
    if (bypassWarnings) {
      splitBodyData.shared = { ...splitBodyData.shared, bypassWarnings: true };
    }

    const expenseData = {
      splits_data_string: encodeURIComponent(
        JSON.stringify({
          ...splitBodyData,
          bypassWarnings
        })
      )
    };

    return apiService
      .post(SplitApi.splitTransactionsApiURL, expenseData)
      .then((response: Response) => response.json() as Promise<{ transactions: IExpenseModel[] }>);
  }

  static splitExistingExpense(splitBodyData: SplitBody, expense: IExpenseModel, bypassWarnings?: boolean) {
    (splitBodyData as any).shared = { ...expense, ...splitBodyData.shared, bypassWarnings: Boolean(bypassWarnings) };

    const expenseData = {
      splits_data_string: encodeURIComponent(
        JSON.stringify({
          ...splitBodyData,
          bypassWarnings
        })
      )
    };

    return apiService
      .post(`/api/web/transactions/${expense.id}/split`, expenseData)
      .then((response: Response) => response.json() as Promise<{ transactions: IExpenseModel[] }>);
  }

  static deleteSplits(splitTransactionId: string): Promise<{ expenses: IExpenseModel[] }> {
    return apiService.responseToJSONAndToastError(apiService.delete(SplitApi.splitTransactionsApiURL + `/${splitTransactionId}`));
  }
}

function createSplitExpensePayload(
  baseValues: ExpenseFormValues | IExpenseModel,
  baseSplit1: ExpenseFormValues | IExpenseModel,
  baseSplit2: ExpenseFormValues | IExpenseModel,
  companyCurrencyCode?: string
) {
  const currencyCode = baseValues.entered_currency_code || 'USD';
  const currencyAmountInMajor = Number(baseValues.entered_currency_amount);

  // split entered currency amount in 2 and assign
  const [splitCurrencyAmount1, splitCurrencyAmount2] = new AbacusCurrency(currencyAmountInMajor, currencyCode, true).split(2);
  baseSplit1.entered_currency_amount = String(splitCurrencyAmount1.getUnitValue());
  baseSplit2.entered_currency_amount = String(splitCurrencyAmount2.getUnitValue());

  // split amount (in USD minor units) in 2 and assign
  const [splitUsdAmount1, splitUsdAmount2] = new AbacusCurrency(baseValues.amount).split(2);
  baseSplit1.amount = splitUsdAmount1.getMinorUnitValue();
  baseSplit2.amount = splitUsdAmount2.getMinorUnitValue();
  baseSplit1.entered_currency_code = currencyCode;
  baseSplit2.entered_currency_code = currencyCode;

  if (baseSplit1.metadata && baseSplit2.metadata) {
    (baseSplit1.metadata as any).currency_amount = String(splitCurrencyAmount1.getUnitValue());
    (baseSplit2.metadata as any).currency_amount = String(splitCurrencyAmount2.getUnitValue());
  }

  if (baseValues.company_currency_amount !== undefined && baseValues.company_currency_amount !== null) {
    // The amount form sets company_currency_amount without always writing company_currency_code,
    // so fall back to the org currency to avoid dropping per-split company currency amounts.
    const [splitCompanyCurrencyAmount1, splitCompanyCurrencyAmount2] = new AbacusCurrency(
      baseValues.company_currency_amount,
      companyCurrencyCode
    ).split(2);
    baseSplit1.company_currency_amount = splitCompanyCurrencyAmount1.getMinorUnitValue();
    baseSplit2.company_currency_amount = splitCompanyCurrencyAmount2.getMinorUnitValue();
    baseSplit1.company_currency_code = companyCurrencyCode;
    baseSplit2.company_currency_code = companyCurrencyCode;
  }
}

function createSplitMileageExpensePayload(
  baseValues: ExpenseFormValues | IExpenseModel,
  baseSplit1: ExpenseFormValues | IExpenseModel,
  baseSplit2: ExpenseFormValues | IExpenseModel,
  companyCurrencyCode?: string,
  companyCurrencyCodeToUsdRate?: number
) {
  const mileageRateMinor = (baseValues.metadata as any).mileage_rate;
  const mileageCurrencyCode =
    (baseValues.metadata as any).currency_type || baseValues.entered_currency_code || companyCurrencyCode || 'USD';

  const splitMileage = parseFloat(((baseValues.metadata as any).mileage / 2).toFixed(2));
  const mileageValue = getMileageValue(splitMileage, mileageRateMinor, mileageCurrencyCode, companyCurrencyCodeToUsdRate);
  baseSplit1.amount = mileageValue.amount;
  baseSplit1.entered_currency_amount = mileageValue.entered_currency_amount;
  baseSplit1.metadata = { ...baseSplit1.metadata, ...mileageValue.metadata };

  const remainingSplitMileage = (baseValues.metadata as any)?.mileage - splitMileage;
  const remainingMileageValue = getMileageValue(remainingSplitMileage, mileageRateMinor, mileageCurrencyCode, companyCurrencyCodeToUsdRate);
  baseSplit2.amount = remainingMileageValue.amount;
  baseSplit2.entered_currency_amount = remainingMileageValue.entered_currency_amount;
  baseSplit2.metadata = { ...baseSplit2.metadata, ...remainingMileageValue.metadata };

  baseSplit1.entered_currency_code = mileageCurrencyCode;
  baseSplit2.entered_currency_code = mileageCurrencyCode;

  if (baseValues.company_currency_amount !== undefined && baseValues.company_currency_amount !== null) {
    // there is potential for inaccurate mismatches as company_currency_amount from the base expense may not split in a mannger that matches distance splits and then conversion
    // if this ambiguity is identified to be an issue we should reconsider how we handle amounts and expressly perform all conversions on the back end to ensure rate consistency.
    const [splitCompanyCurrencyAmount1, splitCompanyCurrencyAmount2] = new AbacusCurrency(
      baseValues.company_currency_amount,
      companyCurrencyCode
    ).split(2);
    baseSplit1.company_currency_amount = splitCompanyCurrencyAmount1.getMinorUnitValue();
    baseSplit2.company_currency_amount = splitCompanyCurrencyAmount2.getMinorUnitValue();
    baseSplit1.company_currency_code = companyCurrencyCode;
    baseSplit2.company_currency_code = companyCurrencyCode;
  } else {
    baseSplit1.company_currency_amount = undefined;
    baseSplit2.company_currency_amount = undefined;
  }
}

export function splitExpense(baseValues?: ExpenseFormValues | IExpenseModel | null, companyCurrencyCodeToUsdRate?: number) {
  if (!baseValues) {
    return [];
  }

  const baseSplit1 = { ...cloneDeep(baseValues), key: uuid() };
  const baseSplit2 = { ...cloneDeep(baseValues), key: uuid() };

  if (baseValues.company_currency_amount === null) {
    baseSplit1.company_currency_amount = undefined;
    baseSplit2.company_currency_amount = undefined;
  }

  const companyCurrencyCode = baseValues.company_currency_code || window.ORG?.currency_code || 'USD';

  if (!baseValues.metadata?.mileage) {
    createSplitExpensePayload(baseValues, baseSplit1, baseSplit2, companyCurrencyCode);
  }

  // Set mileage
  if ((baseValues.metadata as any)?.mileage) {
    createSplitMileageExpensePayload(baseValues, baseSplit1, baseSplit2, companyCurrencyCode, companyCurrencyCodeToUsdRate);
  }

  return [baseSplit1, baseSplit2] as ExpenseFormValues[];
}
